import React, { useState, useEffect } from 'react';
import './IFMS.css';

const IFMS = () => {
  const [activeTab, setActiveTab] = useState('tab1');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 100;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll function
  const handleSmoothScroll = (targetId) => {
    const targetElement = document.querySelector(targetId);
    if (targetElement) {
      window.scrollTo({
        top: targetElement.offsetTop - 80,
        behavior: 'smooth',
      });
      setMobileMenuOpen(false);
    }
  };

  // Tab content data
  const tabContents = {
    tab1: [
      {
        icon: 'fa-solid fa-trash',
        title: 'Daily Cleaning',
        description: 'Making the bed, tidying clutter, wiping down kitchen counters and sinks, and managing dishes and trash.'
      },
      {
        icon: 'fa-solid fa-rotate-right',
        title: 'Periodical Cleaning',
        description: 'Involves scheduled, deeper cleaning tasks that go beyond daily chores, performed at intervals like weekly, monthly, or quarterly.'
      },
      {
        icon: 'fas fa-clipboard-check',
        title: 'Special Cleaning',
        description: 'Occasional, in-depth cleaning tasks that go beyond the daily or weekly routine, such as cleaning after renovations or in response to specific needs.'
      },
      {
        icon: 'fas fa-clipboard-check',
        title: 'Segment Cleaning',
        description: 'Dividing tasks into sequential steps like preparation, dusting, cleaning surfaces, sanitizing, floor care, and final checks for maximum efficiency.'
      }
    ],
    tab2: [
      {
        icon: 'fa-regular fa-building',
        title: 'Front Office',
        description: 'Front office services are customer-facing functions that serve as a company\'s first point of contact, handling tasks like greeting visitors and managing inquiries.'
      },
      {
        icon: 'fa-solid fa-hand-back-fist',
        title: 'Back Office',
        description: 'Back office services are the administrative and support functions that do not directly interact with customers, encompassing tasks like accounting, payroll, and HR.'
      },
      {
        icon: 'fa-solid fa-users-line',
        title: 'Welfare Facilities',
        description: 'Welfare facilities are resources and services provided for the health, safety, and well-being of individuals, particularly in a workplace context.'
      },
      {
        icon: 'fa-solid fa-person-circle-check',
        title: 'Labour Supply',
        description: 'Labour supply is the total hours or number of people willing to work at a given wage rate, influenced by factors such as the real wage rate and opportunities for overtime.'
      }
    ],
    tab3: [
      {
        icon: 'fa-solid fa-person-digging',
        title: 'Building Maintenance',
        description: 'Building maintenance includes cleaning, repairs, and upkeep of the physical structure and its systems to ensure safety and functionality.'
      },
      {
        icon: 'fa-solid fa-campground',
        title: 'Grounds Maintenance',
        description: 'Grounds maintenance for a building involves the care and upkeep of outdoor areas to ensure they are functional, safe, and visually appealing.'
      },
      {
        icon: 'fa-solid fa-lightbulb',
        title: 'Energy Management',
        description: 'Energy in buildings is used for heating, cooling, lighting, appliances, and hot water, representing a significant portion of global energy consumption.'
      },
      {
        icon: 'fa-solid fa-house-chimney-crack',
        title: 'Damage Control',
        description: 'Regularly inspect and maintain plumbing, electrical, and roofing systems to fix leaks and other issues early, preventing costly damage.'
      }
    ],
    tab4: [
      {
        icon: 'fa-solid fa-utensils',
        title: 'Contract Catering',
        description: 'A catering contract is a legally binding agreement between a caterer and a client that details the services to be provided, menus, staffing, and associated costs.'
      },
      {
        icon: 'fa-solid fa-hand-holding-hand',
        title: 'Vending Services',
        description: 'Use automated machines to provide on-demand access to a variety of items, from snacks and beverages to hot and fresh meals in various locations.'
      },
      {
        icon: 'fa-solid fa-handshake',
        title: 'Events Catering',
        description: 'Event catering services provide food and beverages for various occasions, handling everything from menu planning and preparation to setup, service, and cleanup.'
      },
      {
        icon: 'fa-solid fa-cake-candles',
        title: 'Confectionery Services',
        description: 'Confectionery services refers to the production, sales, and support for a wide range of sweet food items for various events and occasions.'
      }
    ],
    tab5: [
      {
        icon: 'fa-solid fa-person-military-rifle',
        title: 'Physical Security',
        description: 'Physical Security Services protect people, property, and assets from physical threats through a combination of personnel, technology, and procedures.'
      },
      {
        icon: 'fa-solid fa-square-poll-horizontal',
        title: 'Surveillance',
        description: 'Use electronic and physical methods to monitor locations for safety and security, deterring crime through visible systems like cameras and sensors.'
      },
      {
        icon: 'fa-solid fa-microchip',
        title: 'Technical Installations',
        description: 'Setting up and configuring technological solutions, ranging from simple software installations to complex network and machinery setups for security systems.'
      },
      {
        icon: 'fa-solid fa-house-fire',
        title: 'Workplace Emergency',
        description: 'A workplace emergency is an unforeseen situation that threatens employees, customers, or the public, disrupts operations, or causes physical or environmental damage.'
      }
    ]
  };

  // Features data
  const features = [
    {
      icon: 'fas fa-broom',
      title: 'Cleaning Services',
      description: 'Prime Ventures Global provides cleaning services through our booking platform by partnering with 5-star rated cleaning partners. We offer premium cleaning services and bulk-buy cleaning packages, which include dusting, vacuuming, and wiping surfaces, tailored to your specific needs.'
    },
    {
      icon: 'fas fa-headset',
      title: 'Support Services',
      description: 'These services can include administrative, logistical, marketing, technical, or customer-focused functions that allow a primary service to run more smoothly and efficiently.'
    },
    {
      icon: 'fa-solid fa-brush',
      title: 'Property Services',
      description: 'Encompass a wide range of tasks to keep buildings safe, functional, and visually appealing, including routine cleaning, general repairs, and upkeep of systems like electrical and plumbing.'
    },
    {
      icon: 'fa-solid fa-bowl-food',
      title: 'Catering Services',
      description: 'Provide food and beverages for groups at remote or specific locations, such as weddings, parties, or business events. This includes preparation, delivery, and often service and cleanup, delivered in various formats.'
    },
    {
      icon: 'fa-solid fa-user-shield',
      title: 'Security Services',
      description: 'Provides trained personnel who protect people, property, and assets through a visible presence, access control, emergency response, and threat assessment.'
    },
    {
      icon: 'fas fa-tools',
      title: 'Maintenance Services',
      description: 'Comprehensive maintenance solutions for all your facility needs, including preventive maintenance, repairs, and system upgrades to ensure optimal performance and longevity.'
    }
  ];

  // Stats data
  const stats = [
    { number: '150+', text: 'Clients Served' },
    { number: '24/7', text: 'Support Available' },
    { number: '98%', text: 'Client Satisfaction' },
    { number: '15+', text: 'Years Experience' }
  ];

  // Floating cards data
  const floatingCards = [
    {
      icon: 'fa-solid fa-kitchen-set',
      title: 'Catering Services',
      description: 'We Bring Culinary Excellence And Seamless Service To Your Event.'
    },
    {
      icon: 'fa-solid fa-user-shield',
      title: 'Security Services',
      description: 'Secure Your Peace Of Mind With Our Expert Protection.'
    },
    {
      icon: 'fas fa-broom',
      title: 'Cleaning Services',
      description: 'Implies Shine And a High Level Of Cleanliness.'
    }
  ];

  return (
    <div className="ifms">
      {/* Header */}
      <header id="ifms-header" className={scrolled ? 'ifms-scrolled' : ''}>
        <div className="ifms-container">
          <nav className="ifms-navbar">
            <a href="#" className="ifms-logo">
              <i className="fas fa-building"></i>
              <div className="ifms-logo-text">
                <span className="ifms-company-name">Prime Ventures Global</span>
                <span className="ifms-company-tagline">Integrated Facility Management Solutions</span>
              </div>
            </a>
            <ul className={`ifms-nav-links ${mobileMenuOpen ? 'ifms-active' : ''}`}>
              <li><a href="#home" onClick={(e) => { e.preventDefault(); handleSmoothScroll('#ifms-home'); }}>Home</a></li>
              <li><a href="#features" onClick={(e) => { e.preventDefault(); handleSmoothScroll('#ifms-features'); }}>Services</a></li>
              <li><a href="#services" onClick={(e) => { e.preventDefault(); handleSmoothScroll('#ifms-services'); }}>Solutions</a></li>
              <li><a href="#contact" onClick={(e) => { e.preventDefault(); handleSmoothScroll('#ifms-contact'); }}>Contact</a></li>
            </ul>
            <div className="ifms-mobile-menu" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <i className="fas fa-bars"></i>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="ifms-hero" id="ifms-home">
        <div className="ifms-container">
          <div className="ifms-hero-content">
            <div className="ifms-hero-text">
              <h1>Integrated Facility Management Services</h1>
              <p>
                Streamline facility management, enhance operational efficiency, and boost 
                institutional performance with our comprehensive platform designed for today's facility challenges.
              </p>
              <div className="ifms-hero-buttons">
                <a href="tel:+91 72999 82022" className="ifms-btn">Call Us Now</a>
                <a href="mailto:support@primeventuresglobal.com" className="ifms-btn ifms-btn-outline">Email Us</a>
              </div>
            </div>
            <div className="ifms-hero-visual">
              <div className="ifms-hero-visual-container">
                {floatingCards.map((card, index) => (
                  <div key={index} className="ifms-floating-card">
                    <div className="ifms-card-icon">
                      <i className={card.icon}></i>
                    </div>
                    <div className="ifms-card-title">{card.title}</div>
                    <div className="ifms-card-desc">
                      {card.description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="ifms-stats">
        <div className="ifms-container">
          <div className="ifms-stats-container">
            {stats.map((stat, index) => (
              <div key={index} className="ifms-stat-item">
                <div className="ifms-stat-number">{stat.number}</div>
                <div className="ifms-stat-text">{stat.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="ifms-features" id="ifms-features">
        <div className="ifms-container">
          <div className="ifms-section-title">
            <h2>What We Offer</h2>
            <p>
              Our platform integrates all aspects of facility management into one seamless experience
            </p>
          </div>
          <div className="ifms-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="ifms-feature-card">
                <div className="ifms-feature-icon">
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrated Services Section */}
      <section className="ifms-integrated-services" id="ifms-services">
        <div className="ifms-container">
          <div className="ifms-section-title">
            <h2>Integrated Facility Services</h2>
            <p>
              Our platform brings together all essential facility services in one unified ecosystem
            </p>
          </div>

          <div className="ifms-services-tabs">
            <button 
              className={`ifms-tab-btn ${activeTab === 'tab1' ? 'ifms-active' : ''}`} 
              onClick={() => setActiveTab('tab1')}
            >
              Cleaning Services
            </button>
            <button 
              className={`ifms-tab-btn ${activeTab === 'tab2' ? 'ifms-active' : ''}`} 
              onClick={() => setActiveTab('tab2')}
            >
              Support Services
            </button>
            <button 
              className={`ifms-tab-btn ${activeTab === 'tab3' ? 'ifms-active' : ''}`} 
              onClick={() => setActiveTab('tab3')}
            >
              Property Services
            </button>
            <button 
              className={`ifms-tab-btn ${activeTab === 'tab4' ? 'ifms-active' : ''}`} 
              onClick={() => setActiveTab('tab4')}
            >
              Catering Services
            </button>
            <button 
              className={`ifms-tab-btn ${activeTab === 'tab5' ? 'ifms-active' : ''}`} 
              onClick={() => setActiveTab('tab5')}
            >
              Security Services
            </button>
          </div>

          {Object.entries(tabContents).map(([tabId, services]) => (
            <div 
              key={tabId} 
              className={`ifms-tab-content ${activeTab === tabId ? 'ifms-active' : ''}`} 
              id={tabId}
            >
              <div className="ifms-service-grid">
                {services.map((service, index) => (
                  <div key={index} className="ifms-service-item">
                    <div className="ifms-service-icon">
                      <i className={service.icon}></i>
                    </div>
                    <div className="ifms-service-content">
                      <h4>{service.title}</h4>
                      <p>{service.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="ifms-cta" id="ifms-contact">
        <div className="ifms-container">
          <div className="ifms-cta-content">
            <h2>Ready to Transform Your Facility Management?</h2>
            <p>
              Contact us today to learn how our integrated services can streamline your operations, reduce costs, and improve service quality.
            </p>
            <div className="ifms-hero-buttons">
              <a href="tel:+91 72999 82022" className="ifms-btn">Call Us Now</a>
              <a href="mailto:support@primeventuresglobal.com" className="ifms-btn ifms-btn-outline">Email Us</a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="ifms-footer">
        <div className="ifms-container">
          <div className="ifms-footer-grid">
            <div className="ifms-footer-column">
              <h3>Prime Ventures Global</h3>
              <p>
                Integrated facility services platform for modern businesses. Empowering organizations through comprehensive, connected facility management solutions.
              </p>
              <div className="ifms-social-links">
                <a href="#"><i className="fab fa-facebook-f"></i></a>
                <a href="#"><i className="fab fa-twitter"></i></a>
                <a href="#"><i className="fab fa-linkedin-in"></i></a>
                <a href="#"><i className="fab fa-instagram"></i></a>
              </div>
            </div>
            <div className="ifms-footer-column">
              <h3>Our Services</h3>
              <ul className="ifms-footer-links">
                <li><a href="#ifms-services">Cleaning Services</a></li>
                <li><a href="#ifms-services">Support Services</a></li>
                <li><a href="#ifms-services">Property Services</a></li>
                <li><a href="#ifms-services">Catering Services</a></li>
                <li><a href="#ifms-services">Security Services</a></li>
              </ul>
            </div>
            <div className="ifms-footer-column">
              <h3>Quick Links</h3>
              <ul className="ifms-footer-links">
                <li><a href="#ifms-home" onClick={(e) => { e.preventDefault(); handleSmoothScroll('#ifms-home'); }}>Home</a></li>
                <li><a href="#ifms-features" onClick={(e) => { e.preventDefault(); handleSmoothScroll('#ifms-features'); }}>Services</a></li>
                <li><a href="#ifms-services" onClick={(e) => { e.preventDefault(); handleSmoothScroll('#ifms-services'); }}>Solutions</a></li>
                <li><a href="#ifms-contact" onClick={(e) => { e.preventDefault(); handleSmoothScroll('#ifms-contact'); }}>Contact</a></li>
              </ul>
            </div>
            <div className="ifms-footer-column">
              <h3>Contact Info</h3>
              <ul className="ifms-footer-links">
                <li><i className="fas fa-map-marker-alt"></i> 86/87 <br/>3rd ,Street SBI Colony ,<br/>Virugambakkam ,<br/>Chennai-600092.</li>
                <li><i className="fas fa-phone"></i> +91 72999 82022</li>
                <li><i className="fas fa-envelope"></i>hr@primeventuresglobal.com,<br/>support@primeventuresglobal.com</li>
                <li><i className="fas fa-clock"></i> Mon-Fri: 9AM-6PM</li>
              </ul>
            </div>
          </div>
          <div className="ifms-footer-bottom">
            <p>
              &copy; 2023 Prime Ventures Global. All rights reserved. | Privacy Policy | Terms of Service
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default IFMS;